import { useContext, useMemo, useState } from "react"
import { CornerLeftDown, Eye } from "lucide-react"
import { Button } from "@components/common/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@components/common/card"
import { Input } from "@components/common/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@components/common/table"

import { ALL_MAIN_BOQ_ENDPOINT, FINANCIAL_SERVER_URL } from "@pma/utils/api-routes"
import axios from "axios"
import { useAuth } from "@pma/stores/auth-store"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { useQuery } from "@tanstack/react-query"
import { DictType } from "@pma/types"
import { Link } from "react-router-dom"
import {
  FINANCIAL_BOQ_MAIN_ROUTE,
  PROJECTS_PAGE_ROUTE,
  USERS_PAGE_ROUTE,
} from "@pma/utils/page-routes"
import useTranslator from "@pma/hooks/use-translator"
import { formatCurrency, getFilteredProjectsByCurrentUser } from "@pma/utils/utils"
import { formatDistance } from "date-fns"
import { he } from "date-fns/locale"
import CreateMainBoQ from "./create-main-boq"
import PmsLoader from "@components/common/loader"
import { cn } from "@pma/utils/helpers"
import { Switch } from "../common/switch"
import RestoreMainBOQ from "./restore-main-boq"
import { UserRoleContext } from "@pma/contexts/UserRoleContext"

interface Props {
  contractorId: string
}
export function ContractorMainBoQTable({ contractorId }: Props) {
  const [searchQuery, setSearchQuery] = useState("")
  const { translate, locale } = useTranslator()

  const { authConfig } = useAuth()

  const accessToken = parseCookies()[ACCESS_TOKEN]

  const orgId = authConfig?.last_organization_id

  const isHebrew = locale === "he"

  const isOrganizationOwner = authConfig?.workspace?.owner_id === authConfig?.id
  const isAdmin = authConfig?.companies_of_user?.find((company: any) => {
    if (company?.id === authConfig?.last_organization_id && company?.role?.id == 22 /* ADMIN_ROLE_ID */) return true
  })

  const [showDeleted, setShowDeleted] = useState(false)

  const { userRoleDetails } = useContext(UserRoleContext)

  const { data: projects } = useQuery({
    queryKey: ["projects", showDeleted],
    queryFn: () =>
      getFilteredProjectsByCurrentUser({
        by_organization_id: orgId,
        status_id: 0,
        paging_per_page: 30,
      }),
  })

  const getMainBoQs = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + ALL_MAIN_BOQ_ENDPOINT,
      {
        organization_id: orgId,
        companies_id: contractorId,
        show_deleted: showDeleted,
      },
      {
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const {
    data: mainBoQ,
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["main-boq", orgId, showDeleted],
    queryFn: getMainBoQs,
  })

  const filteredMainBoQ = useMemo(() => {
    return mainBoQ?.filter((boq: DictType) =>
      boq.name.toLowerCase().includes(searchQuery.toLowerCase()),
    )
  }, [searchQuery, isFetching, mainBoQ])

  const relatedProject = (projectId: number) => {
    return projects?.items?.find((project: DictType) => project?.id === projectId)
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{translate("Main BoQs")}</CardTitle>
          <CardDescription>{translate("Contractor bill of quantities")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className='mb-4 flex items-center justify-between'>
            <div className='flex items-center gap-2'>
              <Input
                placeholder={translate("Search BoQs") + "..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className='w-[300px]'
              />
            </div>
            <div className='flex gap-2'>
              <div className='flex items-center'>
                <div className='flex items-center gap-2'>
                  <Switch
                    checked={showDeleted}
                    className='h-6 w-11'
                    onCheckedChange={(checked) => setShowDeleted(checked)}
                  />
                  <h4 className='text-sm font-semibold'>{translate("Show Deleted Main BOQ")}</h4>
                </div>
              </div>
              {userRoleDetails?.supervisor || userRoleDetails?.fin_manager ? (
                <CreateMainBoQ companyId={String(contractorId)} revalidate={refetch} />
              ) : null}
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{translate("Name")}</TableHead>
                <TableHead>{translate("Project")}</TableHead>
                <TableHead>{translate("Company")}</TableHead>
                <TableHead>{translate("Discount")}</TableHead>
                <TableHead>{translate("Sum")}</TableHead>
                <TableHead>{translate("Created")}</TableHead>
                <TableHead className='w-[80px]'>{translate("Actions")}</TableHead>
              </TableRow>
            </TableHeader>
            {filteredMainBoQ?.length > 0 && !isLoading && (
              <TableBody>
                {filteredMainBoQ?.map((boq: DictType) => (
                  <TableRow key={boq.id} className={cn(boq.deleted ? "bg-red-100" : "")}>
                    <TableCell className={cn("font-medium")}>{boq?.name}</TableCell>
                    <TableCell>
                      <Link to={`${PROJECTS_PAGE_ROUTE}/${boq?.project_id}`}>
                        <Button variant={"link"}>
                          {relatedProject(boq?.project_id)?.title || `#${boq.project_id}`}{" "}
                        </Button>{" "}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Link to={USERS_PAGE_ROUTE + `/${boq?.designer_id}`}>
                        <Button variant={"link"} size='sm' className='flex gap-2 p-0'>
                          #{boq?.company_id}
                        </Button>
                      </Link>
                    </TableCell>
                    <TableCell>
                      <div className='flex w-full gap-2'>
                        <span>{formatCurrency(boq?.discount_sum, isHebrew)}</span>
                        <small className='text-slate-600'>({boq?.discount}%)</small>
                      </div>
                    </TableCell>
                    <TableCell>{formatCurrency(boq?.boq_sum, isHebrew)}</TableCell>
                    <TableCell>
                      {formatDistance(boq?.created_at, new Date(), {
                        locale: isHebrew ? he : undefined,
                        addSuffix: true,
                      })}
                    </TableCell>

                    <TableCell className='flex gap-2'>
                      <Link to={FINANCIAL_BOQ_MAIN_ROUTE + `/${boq?.id}`}>
                        <Eye size={16} className='cursor-pointer' />
                      </Link>{" "}
                      {boq.deleted &&
                      (isOrganizationOwner || isAdmin || boq.deleted_by === authConfig.id) ? (
                        <div>
                          <RestoreMainBOQ boq={boq} refetch={refetch}>
                            <CornerLeftDown className='cursor-pointer text-primary' size={16} />
                          </RestoreMainBOQ>
                        </div>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
          {isLoading && <PmsLoader />}
          {filteredMainBoQ?.length < 1 && !isLoading && (
            <div className='flex w-full items-center justify-center py-4 text-sm font-medium'>
              <p>{translate("No items found")}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  )
}
